import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { initPushNotifications, unregisterPushNotifications } from '../utils/pushNotifications';

console.log('🔍 API URL:', process.env.REACT_APP_API_URL);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Response interceptor to handle 401 (unauthorized) globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - clear auth state
          setToken(null);
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          // Navigation handled by route guards checking !user
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Check for existing session
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        // Re-register push notifications on app reopen
        initPushNotifications(storedToken);
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (username, password, userType, rememberMe = false) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        {
          username,
          password,
          user_type: userType,
          remember_me: rememberMe
        },
        {
          timeout: 30000  // 30 seconds timeout for login
        }
      );

      const { access_token, user_type, user_id, email, is_active } = response.data;

      const userData = {
        id: user_id,
        user_type: user_type,
        email: email,
        is_active: is_active !== false  // Default to true if not provided
      };

      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Initialize push notifications on native platforms
      initPushNotifications(access_token);

      return { success: true, userType: user_type };
    } catch (error) {
      console.error('Login error:', error);

      // Handle timeout specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return {
          success: false,
          error: 'Login request timed out. Please check your connection and try again.'
        };
      }

      // Handle network errors
      if (!error.response) {
        return {
          success: false,
          error: 'Unable to connect to server. Please check your internet connection.'
        };
      }

      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

const signup = async (userData, userType) => {
  try {
    const endpoint = userType === 'driver' 
      ? `${process.env.REACT_APP_API_URL}/api/auth/signup/driver`
      : `${process.env.REACT_APP_API_URL}/api/auth/signup/rider`;
    
    console.log('🔍 Endpoint:', endpoint);
    console.log('🔍 UserData:', userData);
    
    const response = await axios.post(endpoint, userData);

    const { access_token, user_type, user_id, email } = response.data;

    const newUser = {
      id: user_id,
      user_type: user_type,
      email: email
    };

    setToken(access_token);
    setUser(newUser);
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(newUser));

    return { success: true, userType: user_type };
  } catch (error) {
    console.error('Signup error:', error);
    
    // Handle different error response formats
    let errorMessage = 'Signup failed';
    
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Handle Pydantic validation errors (array of error objects)
      if (Array.isArray(errorData.detail)) {
        errorMessage = errorData.detail
          .map(err => `${err.loc.join(' → ')}: ${err.msg}`)
          .join('; ');
      }
      // Handle string error detail
      else if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      }
      // Handle object error detail
      else if (typeof errorData.detail === 'object') {
        errorMessage = JSON.stringify(errorData.detail);
      }
      // Fallback to message field
      else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

  const logout = async () => {
    try {
      // Deactivate push notification token before clearing auth
      await unregisterPushNotifications();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    signup,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};